import { useState, useContext, useEffect } from "react"
import { UserContext } from "../../../context/userContext"
import { DateTime } from "luxon"
import { save } from "save-file"
import HelloSign from "hellosign-embedded"

//PrimeRe3act
import { AccordionTab, Accordion } from "primereact/accordion"
import { ConfirmDialog } from "primereact/confirmdialog"

//Docusign components
import RequestsFilterHeader from "./RequestsFilterHeader"
import RequestsFilters from "./RequestsFilters"
import RequestsViews from "./RequestsViews"
import NewDocumentForm from "./NewRequestForm"

import {
  docSigningApiRequestGet,
  docSigningApiSignatureRequestsListPost,
  docSigningApiHelloSignConfigGet,
  docSigningApiListTemplatesPost,
  docSigningApiSignatureRequestsCancelGet,
  docSigningApiSignatureRequestsDownloadGet,
  docSigningApiSignURLGet,
} from "../documentsApi"
import { GET_USERS_PARTICIPANTS, GET_USERS_FROM_LIST } from "../queries"

//SOAR components
import { LoaderMedium } from "../../../components/Loaders/index"
import { useLazyQuery, useQuery } from "@apollo/client"
import { LogError, LogDebug, LogWarning } from "../../../helpers/logger"
import { HubConnectionBuilder, LogLevel } from "@microsoft/signalr"
import { documentSigningApiUrl } from "../../../config/microservicesUrls"
import { useClientMethod, useHub } from "react-use-signalr"
import { Tooltip } from "primereact/tooltip"
import { useRef } from "react"
import isParticipantActive from "../../../helpers/isParticipantActive"

const Requests = () => {
  const userCtx = useContext(UserContext)
  const isTestMode = process.env.REACT_APP_STAGE === "development"
  const requestSortOptions = [
    { display: "Title", field: "title" },
    { display: "Requested By", field: "requestCreatorName" },
    { display: "Date Requested", field: "requestCreatedAt" },
    { display: "Date Completed", field: "completedDate" },
    { display: "Status", field: "status" },
  ]

  const myStaffProfile = {
    name: `Me - ${userCtx.session.user.staff_name_first} ${userCtx.session.user.staff_name_last}`,
    signerAlias: `${userCtx.session.user.staff_alias}-STAFF`,
    signerId: `${userCtx.session.user.staff_id}-STAFF`,
    email: userCtx.session.user.staff_email_address,
  }

  const [layoutOption, setLayoutOption] = useState("grid")
  const [loading, setLoading] = useState(true)
  const [loadingRequests, setLoadingRequests] = useState(true)
  const [loadingUsers, setLoadingUsers] = useState(true)
  const [appliedFilters, setAppliedFilters] = useState({
    dateCreated: [],
    search: "",
    staff: [myStaffProfile],
    participants: [],
    status: "all",
    sort: requestSortOptions[2],
    sortDirection: "Descending",
  })
  const [appliedQuickFilter, setAppliedQuickFilter] = useState("my_requests")
  const [documents, setDocuments] = useState(null)
  const [deleteDocumentIds, setDeleteDocumentIds] = useState([])
  const [helloSignConfig, setHelloSignConfig] = useState(undefined)
  const [showNewRequest, setShowNewRequest] = useState(false)
  const [templates, setTemplates] = useState([])
  const [participants, setParticipants] = useState([])
  const [showCancelConfirm, setShowCancelConfirm] = useState(false)
  const [cancelRequestId, setCancelRequestId] = useState(undefined)

  const participantList = useRef()
  const staffList = useRef()

  // SignalR
  const [connection, setConnection] = useState(null)
  const { hubConnectionState, error: hubError } = useHub(connection)
  useClientMethod(connection, "HelloSignEvent", (data) => {
    HandleHelloSignEvent(data)
  })

  //Retrieve requests during initial load
  useEffect(() => {
    //This should only be loading at this stage during initial load
    let quickFilters = undefined
    if (loading && !!userCtx.documentsQuickFilter) {
      switch (userCtx.documentsQuickFilter) {
        case "await_my_signature":
          quickFilters = {
            dateCreated: [],
            search: "",
            staff: [`${userCtx.session.user.staff_id}-STAFF`],
            participants: [],
            status: "awaiting_signature",
          }
          break
        default:
          break
      }
    }
    getRequests(undefined, undefined, quickFilters)
    getTemplates()
    getHelloSignConfig()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userCtx.session.user.organization_id])

  const [get_users_in_list] = useLazyQuery(GET_USERS_FROM_LIST, {
    variables: {},
    fetchPolicy: "network-only",
  })

  useQuery(GET_USERS_PARTICIPANTS, {
    variables: {
      staff_id: userCtx.session.user.staff_id,
      organization_id: userCtx.session.user.organization_id,
    },
    fetchPolicy: "network-only",
    onCompleted: ({ allParticipants, myParticipants, users }) => {
      const myParts = myParticipants.filter((participant) =>
        isParticipantActive(participant.participant_membership_status),
      )

      const participants = allParticipants
        .filter((participant) =>
          isParticipantActive(participant.participant_membership_status),
        )
        .map((participant) => {
          return {
            name: `${participant.participant_name_first} ${participant.participant_name_last}`,
            id: participant.participant_id,
            signerId: participant.participant_id,
            signerAlias: participant.participant_alias,
            email: participant.participant_email_account,
            myParticipant: myParts.some(
              (x) => x.participant_id === participant.participant_id,
            ),
          }
        })
        .sort((a, b) => a.name.localeCompare(b.name)) // Ensure participants are sorted by name

      const newStaff = users
        .map((s) => {
          return {
            name: s.staff_name_first + " " + s.staff_name_last,
            id: s.staff_id,
            alias: s.staff_alias,
            signerId: `${s.staff_id}-STAFF`,
            signerAlias: `${s.staff_alias}-STAFF`,
            email: s.staff_email_address,
            role: s.staff_role_name,
          }
        })
        .sort((a, b) => a.name.localeCompare(b.name)) // Ensure staff are sorted by name

      setParticipants([
        { name: "Participants", code: "PA", items: participants },
        { name: "Staff", code: "ST", items: newStaff },
      ])

      participantList.current = participants
      staffList.current = newStaff

      setLoadingUsers(false)
    },
    onError: (error) => {
      LogError("Unable to load user lists", error)
      userCtx.setNotificationError(
        "Unable to load user lists, see console for details",
      )
      setLoadingUsers(false)
    },
  })

  function SetupSignalRConnection() {
    const api_url = documentSigningApiUrl()
    // Setup Connection
    const connection = new HubConnectionBuilder()
      .withUrl(`${api_url}/hubs/hellosign`)
      .configureLogging(isTestMode ? LogLevel.Debug : LogLevel.Warning)
      .withAutomaticReconnect()
      .build()

    connection?.onclose(async () => {
      connection?.start().catch((error) => {
        console.warn(error)
      })
    })

    setConnection(connection) // Save connection to state
  }

  useEffect(() => {
    if (!connection) {
      SetupSignalRConnection()
    }

    // Monitor for SignalR errors
    if (hubError) {
      console.warn(
        "SignalR Warning: ",
        hubError,
        `\nConnection State: ${hubConnectionState}`,
      )
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connection, hubError])

  async function HandleHelloSignEvent(data) {
    const request = data?.signature_request

    if (isTestMode) LogDebug("HS Event Data", data)

    // eslint-disable-next-line default-case
    switch (data.event.eventType) {
      case 13: //Signature request cancelled
        if (
          documents?.items?.some(
            (x) =>
              x.signatureRequestId === data.signatureRequest.signatureRequestId,
          )
        ) {
          userCtx.setNotificationInformation(
            "A signature request cancellation has been processed, please refresh your interface to update the list",
          )
        }
        break
      case 17: //"signature_request_invalid":
      case 3: //"file_error":
      case 2: //"unknown_error":
        if (
          ValidateRequestGroup(
            GetRequestDetails(
              data.signatureRequest?.signatureRequestId,
              request,
            ),
          ) &&
          ValidateRequestUser(request, ["Sender"])
        ) {
          userCtx.setNotificationError(
            "Something went wong while sending a signature-request. Please try again if this was your request. Refresh to update your list.",
          )
          LogError(
            "Something went wong while sending a signature-request. Please try again if this was your request. Refresh to update your list.",
            data,
          )
        }
        break
    }
  }

  async function GetRequestDetails(signatureRequestId, request) {
    try {
      if (!request || !request.metadata) {
        return await docSigningApiRequestGet(
          userCtx.tokens.getDocumentSignToken(),
          signatureRequestId,
        )
      } else {
        return request
      }
    } catch (error) {
      LogError("Unable to get signature request details.", error)
      userCtx.setNotificationError(
        "Unable to get signature request details. See console for details.",
      )
    }
  }

  async function ValidateRequestGroup(signatureRequest) {
    try {
      if (signatureRequest) {
        // Extract GID
        const tGID = signatureRequest.metadata?.GroupId?.split("||")[0]

        // Compare GID
        if (tGID === userCtx.session.user.organization_id) {
          return true
        }
      }
    } catch (error) {
      LogWarning("Unable to validate request group", error)
    }

    return false
  }

  // userKey = array of "Sender" and/or "Signer"
  //   ex.1: ["Sender"]
  //   ex.2: ["Sender", "Signer"]
  //   ex.3: ["Signer"]
  function ValidateRequestUser(signatureRequest, userKey) {
    try {
      if (signatureRequest) {
        for (const key of Object.keys(signatureRequest.metadata)) {
          if (
            signatureRequest.metadata[key].includes(userKey[0]) ||
            signatureRequest.metadata[key].includes(userKey[1])
          ) {
            // Extract ID
            const userId = signatureRequest.metadata[key]
              .split("||")[0]
              ?.replace("-STAFF", "")
            if (userId && userId === userCtx.session.user.staff_id) {
              return true
            }
          }
        }
      }
    } catch (error) {
      LogWarning("Unable to validate user", error)
    }

    return false
  }

  const stripMetaKey = (value) => {
    try {
      return value.split("||")[0]
    } catch (error) {
      return value
    }
  }

  const assignedToMe = (request) => {
    let mySignature = undefined
    const mySignatureId = `${userCtx.session.user.staff_id}-STAFF`
    const mySignatureAlias = `${userCtx.session.user.staff_alias}-STAFF`
    request.signatures.forEach((signature) => {
      const sigSignerId = stripMetaKey(request.metadata[signature.signerRole])
      if (sigSignerId === mySignatureId || sigSignerId === mySignatureAlias) {
        mySignature = signature
      }
    })

    return mySignature
  }

  const checkUserProfiles = async (requests) => {
    let userIds = []
    requests.forEach((request) => {
      request.signatures.forEach((signature) => {
        if (signature.signerName === signature.signerRole) {
          userIds.push(
            stripMetaKey(request.metadata[signature.signerRole]).replace(
              "-STAFF",
              "",
            ),
          )
        }
      })
    })

    userIds = userIds.filter((id) => {
      return (
        id !== userCtx.session.user.staff_id &&
        !participants.some((users) =>
          users.items.some((user) => user.id === id || user.alias === id),
        )
      )
    })
    userIds = Array.from(new Set(userIds))

    if (userIds.length > 0) {
      let staff_users = await get_users_in_list({
        variables: { list: userIds },
      })

      staff_users = staff_users.data.users
        .filter((user) => {
          return !!user.staff_id
        })
        .map((user) => {
          return {
            name: user.staff_name_first + " " + user.staff_name_last,
            id: user.staff_id,
            alias: user.staff_alias,
            signerId: `${user.staff_id}-STAFF`,
            signerAlias: `${user.staff_alias}-STAFF`,
            email: user.staff_email_address,
            role: user.staff_role_name,
          }
        })

      let newStaffList = { name: "Staff", code: "ST", items: staff_users }
      if (!!staffList.current) {
        newStaffList.items = [...staffList.current, ...newStaffList.items]
      }

      setParticipants([
        {
          name: "Participants",
          code: "PA",
          items: !!participantList.current ? participantList.current : [],
        },
        newStaffList,
      ])
    }
  }

  const getRequests = async (page, pageSize, filters) => {
    setLoadingRequests(true)
    try {
      const useFilters = filters || appliedFilters

      let groupIds = [userCtx.session.user.organization_id]
      //Negative Ids causes exceptions on HelloSign API, only send valid alias values
      if (userCtx.session.user.organization_alias > 0) {
        groupIds.push(userCtx.session.user.organization_alias)
      }

      // Build request
      const requestBody = {
        groupIds: groupIds,
        pageNum: page || 1,
        pageSIze: pageSize || 101,
      }

      if (useFilters.dateCreated && useFilters.dateCreated.length > 0) {
        let eopFilter = !!useFilters.dateCreated[1]
          ? useFilters.dateCreated[1]
          : useFilters.dateCreated[0]
        eopFilter.setHours(23)
        eopFilter.setMinutes(59)
        eopFilter.setSeconds(59)

        requestBody.createDateFilter = [
          DateTime.fromJSDate(useFilters.dateCreated[0]).toUTC().toISO(),
          DateTime.fromJSDate(eopFilter).toUTC().toISO(),
        ]
      }

      if (useFilters.search) {
        requestBody.searchFilter = useFilters.search
      }

      if (useFilters.status) {
        requestBody.statusFilter = useFilters.status
      }

      if (useFilters.staff || useFilters.participants) {
        requestBody.recipientIds = [
          ...useFilters.staff.map((staff) => staff.signerId),
          ...useFilters.participants.map((participant) => participant.signerId),
        ]

        requestBody.recipientEmails = [
          ...useFilters.participants.map((participant) => participant.email),
          ...useFilters.staff.map((staff) => staff.email),
        ]
      }

      // Execute request
      let data = await docSigningApiSignatureRequestsListPost(
        userCtx.tokens.getDocumentSignToken(),
        requestBody,
      )

      if (data.items?.length > 0) {
        //Reset structure to expected layout
        data.items = data.items.map((request) => {
          return {
            ...request.signatureRequest,
            numSignersComplete: request.numSignersComplete,
            numSignersTotal: request.numSignersTotal,
          }
        })

        //Check for staff user profiles that might be missing from the cache and retrieve them
        await checkUserProfiles(data.items)

        const getStatus = (request) => {
          let status = "No status available"

          if (request.isComplete) {
            status = "Complete"
          } else if (request.hasError) {
            status = "Error"
          } else if (request.numSignersComplete < request.numSignersTotal) {
            status = `Awaiting signature (${request.numSignersComplete} of ${request.numSignersTotal} complete)`
          }

          return status
        }

        const getOrder = (request) => {
          if (!request.assignedToMe) {
            return "N/A"
          } else {
            return `${request.mySignatureRequest?.order || "0"} ${
              request.mySignatureRequest?.order === 1
                ? "person needs"
                : "people need"
            }  
              to sign before you.`
          }
        }

        data.items.forEach((request) => {
          request.mySignatureRequest = assignedToMe(request)
          request.assignedToMe = !!request.mySignatureRequest
          request.status = getStatus(request)
          request.canSignNow =
            request.assignedToMe &&
            request.numSignersComplete >= request.mySignatureRequest?.order &&
            request.mySignatureRequest.statusCode === "awaiting_signature"
          request.orderText = getOrder(request)
          request.requestCreatorId = stripMetaKey(request?.metadata?.Sender)
          request.requestCreatorName = stripMetaKey(
            request?.metadata?.SenderName,
          )
          //created date value
          request.requestCreatedAt = new Date(
            stripMetaKey(request?.metadata?.CreatedAt),
          )
          //Created display string
          request.requestCreatedAtLocal =
            request.requestCreatedAt.toLocaleString()

          //Find latest signature date
          request.completedDate = undefined
          request.completedDateLocal = "Not Completed"

          //Find the latest signed date
          request.signatures?.forEach((signature) => {
            if (signature?.statusCode === "signed" && signature.signedAt) {
              const completedDate = new Date(signature.signedAt * 1000)
              if (
                request.completedDate === undefined ||
                completedDate > request.completedDate
              ) {
                request.completedDate = completedDate
                request.completedDateLocal = completedDate.toLocaleString()
              }
            }
          })
        })
      }

      //Sort data
      data = sortRequests(
        appliedFilters.sort,
        appliedFilters.sortDirection,
        data,
      )

      // Set data
      setDocuments(data)
      setLoadingRequests(false)
      setLoading(false)
      return data
    } catch (error) {
      LogError("Unable to list requests", error)
      userCtx.setNotificationError(
        "Unable to list signature requests. See console for details.",
      )
      setDocuments([])
      setLoadingRequests(false)
      setLoading(false)
    }
  }

  function sortRequests(sortOption, sortDirection, requests) {
    let sortedRequests = JSON.parse(JSON.stringify(requests))
    sortedRequests.items.forEach((request) => {
      request.requestCreatedAt = new Date(request.requestCreatedAt)
      request.completedDate = !!request.completedDate
        ? new Date(request.completedDate)
        : undefined
    })

    const sortField = sortOption.field

    if (sortDirection === "Descending") {
      sortedRequests.items.sort((a, b) => {
        const result =
          a[sortField] > b[sortField] ? -1 : a[sortField] < b[sortField] ? 1 : 0
        return result
      })
    } else {
      sortedRequests.items.sort((a, b) => {
        const result =
          a[sortField] < b[sortField] ? -1 : a[sortField] > b[sortField] ? 1 : 0
        return result
      })
    }

    return sortedRequests
  }

  const getTemplates = async (page, pageSize, filters) => {
    setLoading(true)
    try {
      // Build request
      const requestBody = {
        groupIds: [userCtx.session.user.organization_id],
        pageNum: page || 1,
        pageSIze: pageSize || 10,
      }

      if (userCtx.session.user.organization_alias > 0) {
        requestBody.groupIds.push(userCtx.session.user.organization_alias)
      }

      // Execute request
      const data = await docSigningApiListTemplatesPost(
        userCtx.tokens.getDocumentSignToken(),
        requestBody,
      )

      // Set data
      setTemplates(data)
      setLoading(false)
      return data
    } catch (error) {
      LogError("Unable to list templates.", error)
      userCtx.setNotificationError(
        "Unable to list templates. See console for details.",
      )
      setTemplates([])
      setLoading(false)
    }
  }

  const getHelloSignConfig = async () => {
    try {
      const config = await docSigningApiHelloSignConfigGet(
        userCtx.tokens.getDocumentSignToken(),
      )
      setHelloSignConfig(config)
    } catch (error) {
      LogError(error)
      userCtx.setNotificationError(
        "Unable to retrieve Hello Sign configuration. See console for details.",
      )
    }
  }

  const cancelRequest = (requestId) => {
    setCancelRequestId(requestId)
    setShowCancelConfirm(true)
  }

  const applyCancellation = async () => {
    try {
      if (!deleteDocumentIds.includes(cancelRequestId)) {
        setDeleteDocumentIds([...deleteDocumentIds, cancelRequestId])

        await docSigningApiSignatureRequestsCancelGet(
          userCtx.tokens.getDocumentSignToken(),
          cancelRequestId,
        )
        userCtx.setNotificationInformation(
          "Your signature request cancellation has been successfully submitted and is being processed.",
        )
      }
    } catch (error) {
      let message =
        "Unable to cancel signature request. See console for details."

      // Check for specific error massages
      if (
        error.message.toLowerCase().includes("this resource has been deleted")
      ) {
        message =
          "Unable to cancel signature request. The request is no longer available. Please refresh your list."
      }

      LogError(message, error)
      userCtx.setNotificationError(message)
    }
  }

  const downloadRequestDocument = async (signatureRequestId) => {
    setLoading(true)
    try {
      const res = await docSigningApiSignatureRequestsDownloadGet(
        userCtx.tokens.getDocumentSignToken(),
        signatureRequestId,
      )
      save(res, `${signatureRequestId}.pdf`)
      setLoading(false)
    } catch (error) {
      userCtx.setNotificationError(
        `Unable to download signature request documents. See console for details.`,
      )
      LogError("Unable to download signature request documents", error)
      setLoading(false)
    }
  }

  function blockZooming() {
    const viewport = document.querySelector("meta[name=viewport]")
    if (viewport) {
      let content = viewport.getAttribute("content") || ""
      const newContent = content.split(/,\s?/)
      // Prevent browsers from automatically zooming into text fields
      if (!content.includes("maximum-scale=1")) {
        newContent.push("maximum-scale=1")
        content = newContent.join(", ")
      }
      if (content !== viewport.getAttribute("content")) {
        viewport.setAttribute("content", content)
      }
    }
  }

  const signDocument = async (signatureRequest) => {
    setLoading(true)
    try {
      const res = await docSigningApiSignURLGet(
        userCtx.tokens.getDocumentSignToken(),
        signatureRequest.mySignatureRequest.signatureId,
      )

      if (res && res.embedded.signUrl) {
        const client = new HelloSign({
          clientId: helloSignConfig.clientId,
          skipDomainVerification: helloSignConfig.mode === "Test",
        })

        blockZooming() // work-around for mobile signing
        client.open(res.embedded.signUrl)
      }
      setLoading(false)
    } catch (error) {
      let message = "Unable to sign the request. See console for details."

      if (
        error.message
          ?.toLowerCase()
          .includes("this request has already been signed")
      ) {
        message =
          "Unable to sign the request. This request has already been signed. Please refresh your list"
      }

      if (
        error.message?.toLowerCase().includes("request cannot be signed yet")
      ) {
        message =
          "This request cannot be signed yet. There are other signers that need to sign before you."
      }

      userCtx.setNotificationError(message)
      LogError(message, error)
      setLoading(false)
    }
  }

  return (
    <>
      <Tooltip target=".t-tip" />
      <ConfirmDialog
        visible={showCancelConfirm}
        onHide={() => setShowCancelConfirm(false)}
        message="Are you sure you want to proceed?"
        header="Confirmation"
        icon="pi pi-exclamation-triangle"
        accept={applyCancellation}
        reject={() => setShowCancelConfirm(false)}
      />
      <NewDocumentForm
        showNewRequest={showNewRequest}
        setShowNewRequest={setShowNewRequest}
        templates={templates}
        helloSignConfig={helloSignConfig}
        participants={participants}
      />
      <Accordion style={{ marginBottom: "10px" }} activeIndex={0}>
        <AccordionTab
          header={
            <RequestsFilterHeader
              users={participants}
              myStaffProfile={myStaffProfile}
              layoutOption={layoutOption}
              appliedQuickFilter={appliedQuickFilter}
              setLayoutOptionCallBack={setLayoutOption}
              getRequestsCallBack={getRequests}
              newRequestCallBack={() => {
                setShowNewRequest(true)
              }}
              currentSortOption={appliedFilters.sort}
              currentSortDirection={appliedFilters.sortDirection}
              onSetAppliedFilters={(filters, filterName) => {
                if (
                  filters.dateCreated !== appliedFilters.dateCreated ||
                  filters.search !== appliedFilters.search ||
                  filters.status !== appliedFilters.status ||
                  filters.participants !== appliedFilters.participants ||
                  filters.staff !== appliedFilters.staff
                ) {
                  setAppliedFilters(filters)
                  setAppliedQuickFilter(filterName)
                  getRequests(undefined, undefined, filters)
                }
              }}
              isLoading={loading}
            />
          }
          disabled={loading || loadingUsers || loadingRequests}
        >
          <RequestsFilters
            userOptions={participants}
            myStaffProfile={myStaffProfile}
            filters={appliedFilters}
            sortOptions={requestSortOptions}
            onChangeFilters={(filters) => {
              if (
                filters.dateCreated !== appliedFilters.dateCreated ||
                filters.search !== appliedFilters.search ||
                filters.status !== appliedFilters.status ||
                filters.participants !== appliedFilters.participants ||
                filters.staff !== appliedFilters.staff
              ) {
                setAppliedFilters(filters)
                setAppliedQuickFilter("")
                getRequests(undefined, undefined, filters)
              }
            }}
            onChangeSortField={(option) => {
              const sortedRequests = sortRequests(
                option.sort,
                option.sortDirection,
                documents,
              )
              setDocuments(sortedRequests)

              let updatedFilters = appliedFilters
              updatedFilters.sort = option.sort
              updatedFilters.sortDirection = option.sortDirection
              setAppliedFilters(updatedFilters)
            }}
            isLoading={loading}
          />
        </AccordionTab>
      </Accordion>
      {loading || loadingUsers || loadingRequests ? (
        <LoaderMedium />
      ) : (
        <>
          <RequestsViews
            documents={documents}
            deletedDocumentIds={deleteDocumentIds}
            layoutOption={layoutOption}
            users={participants}
            appliedTableSorting={{
              sortField: appliedFilters.sort?.field,
              sortOrder: appliedFilters.sortDirection === "Descending" ? -1 : 1,
            }}
            onCancelRequest={cancelRequest}
            onDownloadRequest={downloadRequestDocument}
            onSignRequest={signDocument}
            onSortRequests={(fieldName, sortOrder) => {
              let sortOption = requestSortOptions[2]

              switch (fieldName) {
                case "title":
                  sortOption = requestSortOptions[0]
                  break
                case "requestCreatorName":
                  sortOption = requestSortOptions[1]
                  break
                case "completedDate":
                  sortOption = requestSortOptions[3]
                  break
                case "status":
                  sortOption = requestSortOptions[4]
                  break
                default:
                  break
              }

              const direction =
                sortOrder === -1 || sortOrder === -0
                  ? "Descending"
                  : "Ascending"

              setAppliedFilters({
                dateCreated: appliedFilters.dateCreated,
                search: appliedFilters.search,
                staff: appliedFilters.staff,
                participants: appliedFilters.participants,
                status: appliedFilters.status,
                sort: sortOption,
                sortDirection: direction,
              })

              setDocuments(sortRequests(sortOption, direction, documents))
            }}
          />
          {/*  <Paginator
            first={documents?.pageSize * (documents?.pageNum - 1) || 1}
            rows={documents?.pageSize || 10}
            totalRecords={documents?.numResults || 0}
            rowsPerPageOptions={[10, 20, 30]}
            onPageChange={(event) => {
              getRequests(event.page + 1, event.rows)
            }}
          /> */}
        </>
      )}
    </>
  )
}
export default Requests
